import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import { MessageCircle, ShieldCheck, Sparkles, Store } from "lucide-react";
import { Eyebrow, MaskText } from "@/components/motion";
import { COLLECTIONS, CONTACT, REVIEW_COUNT, WHATSAPP } from "@/data/catalog";
import { EASE } from "@/lib/scroll";

const Count = ({ to, prefix = "", pad = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [val, setVal] = useState(to);
  useEffect(() => {
    if (!inView) return undefined;
    const ctrl = animate(0, to, { duration: 1.8, ease: EASE, onUpdate: (v) => setVal(Math.round(v)) });
    return () => ctrl.stop();
  }, [inView, to]);
  return (
    <span ref={ref}>
      {prefix}
      {String(val).padStart(pad, "0")}
    </span>
  );
};

const STATS = [
  { label: "Established in Delhi", node: "2021" },
  { label: "Customer reviews on Judge.me", node: <Count to={REVIEW_COUNT} /> },
  { label: "Collections in the Edit", node: <Count to={COLLECTIONS.length} pad={2} /> },
  { label: "Lashes start at", node: <Count to={199} prefix="₹" /> },
];

const MAPS = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CONTACT.address)}`;

const PILLARS = [
  { icon: Store, title: "Visit our Karol Bagh store", text: CONTACT.address, href: MAPS, id: "store" },
  { icon: MessageCircle, title: "WhatsApp & call support", text: CONTACT.phone, href: WHATSAPP, id: "whatsapp" },
  { icon: ShieldCheck, title: "Secure Shopify checkout", text: "Every bag checks out safely on acevanity.in.", id: "checkout" },
  { icon: Sparkles, title: "Made for makeup artists", text: "Providing MUAs with on-site solutions since 2021.", id: "mua" },
];

export const Proof = () => (
  <section data-testid="social-proof-section" className="relative overflow-hidden border-y border-gold/15 bg-ink-2 py-24 lg:py-32">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <Eyebrow>09 — Why the Beauty Edit</Eyebrow>
      <h2 className="mt-5 max-w-3xl font-display text-5xl font-light leading-[0.95] text-cream sm:text-6xl">
        <MaskText lines={["Trusted by artists,", <em key="e" className="italic text-champagne">loved by their clients.</em>]} />
      </h2>
      <dl className="mt-16 grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 lg:grid-cols-4">
        {STATS.map((s) => (
          <div key={s.label} className="bg-ink-2 p-6 sm:p-8">
            <dd className="font-display text-5xl font-light text-cream sm:text-6xl">{s.node}</dd>
            <dt className="mt-3 text-xs uppercase tracking-[0.16em] text-stone">{s.label}</dt>
          </div>
        ))}
      </dl>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {PILLARS.map(({ icon: Icon, title, text, href, id }, i) => {
          const Tag = href ? "a" : "div";
          return (
            <motion.div
              key={id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.07 }}
            >
              <Tag
                {...(href ? { href, target: "_blank", rel: "noreferrer" } : {})}
                data-testid={`trust-pillar-${id}`}
                className="group flex h-full flex-col gap-4 border border-white/10 p-6 transition-colors duration-500 hover:border-champagne/50"
              >
                <Icon className="h-6 w-6 text-champagne transition-transform duration-500 group-hover:-translate-y-1" />
                <p className="font-display text-2xl text-cream">{title}</p>
                <p className="text-sm leading-relaxed text-stone">{text}</p>
              </Tag>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);
